import type { FC } from 'react';
import { Modal } from '@interstate/components/Modal';
import { Typography } from '@interstate/components/Typography';

interface DiscardChangesModalProps {
    show: boolean;
    onDiscard: () => void;
    onGoBack: () => void;
    hasValidationErrors?: boolean;
    showSimpleDiscard?: boolean;
    message?: string;
    title?: string;
}

const DISCARD_TITLE = 'Warning: Changes Will Be Lost';
const DEFAULT_MESSAGE =
    'You’ve made updates that haven’t been applied. Canceling will discard your changes permanently. Do you want to keep them?';
const VALIDATION_ERROR_TITLE = 'Fix Required Fields to Continue';
const VALIDATION_ERROR_MESSAGE =
    "Some updates can't be saved yet. Review the highlighted fields and correct the errors before continuing.";

export const DiscardChangesModal: FC<DiscardChangesModalProps> = ({
    show,
    onDiscard,
    onGoBack,
    hasValidationErrors = true,
    showSimpleDiscard = false,
    message,
    title
}) => {
    const footerConfig = (() => {
        if (showSimpleDiscard) {
            return {
                layout: 'rightAligned' as const,
                options: {
                    action: onGoBack,
                    label: 'Go Back',
                    buttonStyle: 'tertiary' as const,
                    customIdentifiers: {
                        optionButton0: {
                            dataTestId: 'buyer-cobuyer-goback-btn'
                        }
                    }
                },
                primary: {
                    action: onDiscard,
                    label: 'Discard Changes',
                    customIdentifiers: {
                        primaryButton: {
                            dataTestId: 'buyer-cobuyer-discard-changes-btn'
                        }
                    }
                }
            };
        }

        if (hasValidationErrors) {
            return {
                layout: 'rightAligned' as const,
                options: {
                    action: onDiscard,
                    label: 'Cancel Changes',
                    buttonStyle: 'tertiary' as const,
                    customIdentifiers: {
                        optionButton0: {
                            dataTestId: 'buyer-cobuyer-discard-changes-btn'
                        }
                    }
                },
                primary: {
                    action: onGoBack,
                    label: 'Go Back',
                    customIdentifiers: {
                        primaryButton: {
                            dataTestId: 'buyer-cobuyer-continue-editing-btn'
                        }
                    }
                }
            };
        }

        return {
            layout: 'rightAligned' as const,
            options: {
                action: onDiscard,
                label: 'Cancel Changes',
                buttonStyle: 'tertiary' as const,
                customIdentifiers: {
                    optionButton0: {
                        dataTestId: 'buyer-cobuyer-cancel-changes-btn'
                    }
                }
            },
            primary: {
                action: onGoBack,
                label: 'Continue Editing',
                customIdentifiers: {
                    primaryButton: {
                        dataTestId: 'buyer-cobuyer-continue-editing-btn'
                    }
                }
            }
        };
    })();

    const displayTitle = title || (hasValidationErrors ? VALIDATION_ERROR_TITLE : DISCARD_TITLE);
    const displayMessage = message || (hasValidationErrors ? VALIDATION_ERROR_MESSAGE : DEFAULT_MESSAGE);

    return (
        <Modal
            size="small"
            id="buyer-cobuyer-discard-changes-modal"
            data-testid="buyer-cobuyer-discard-changes-modal"
            show={show}
            onHide={onGoBack}
            header={
                <Typography variant="h4" color="base.color.blue.700">
                    {displayTitle}
                </Typography>
            }
            footer={footerConfig}
        >
            <Typography variant="body-md" tag="span">
                {displayMessage}
            </Typography>
        </Modal>
    );
};
